import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FileText,
  Download,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  ArrowLeft,
  Calendar,
  DollarSign,
  CreditCard,
  Filter,
  RefreshCw
} from 'lucide-react';
import { api } from '../services/api';
import { useToast } from '../contexts/ToastContext';

interface Invoice {
  payment_id: number;
  status: string;
  status_detail: string;
  transaction_amount: number;
  currency_id: string;
  date_created: string;
  date_approved: string | null;
  payment_method_id: string;
  payment_type_id: string;
  installments: number;
  description: string;
}

export const InvoicesPage: React.FC = () => {
  const navigate = useNavigate();
  const { subscriptionId } = useParams();
  const { showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<Invoice[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [yearFilter, setYearFilter] = useState<string>('all');

  const loadInvoices = useCallback(async () => {
    if (!subscriptionId) {
      showError('ID da assinatura não fornecido');
      navigate('/billing');
      return;
    }

    try {
      setLoading(true);
      const response = await api.get(`/payments/subscription/${subscriptionId}/invoices`);

      if (response.data.success && response.data.data.invoices) {
        setInvoices(response.data.data.invoices);
      }
    } catch (error: any) {
      console.error('Erro ao carregar faturas:', error);
      showError(error.response?.data?.message || 'Erro ao carregar histórico de faturas');
    } finally {
      setLoading(false);
    }
  }, [navigate, showError, subscriptionId]);

  const applyFilters = useCallback(() => {
    let filtered = [...invoices];

    // Filtro por status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(inv => inv.status === statusFilter);
    }

    // Filtro por ano
    if (yearFilter !== 'all') {
      filtered = filtered.filter(inv => {
        const year = new Date(inv.date_created).getFullYear().toString();
        return year === yearFilter;
      });
    }

    setFilteredInvoices(filtered);
  }, [invoices, statusFilter, yearFilter]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const getStatusInfo = (status: string) => {
    const statusMap: Record<string, { label: string; color: string; icon: any }> = {
      approved: { label: 'Aprovado', color: '#00d4aa', icon: CheckCircle },
      pending: { label: 'Pendente', color: '#ffa500', icon: Clock },
      rejected: { label: 'Rejeitado', color: '#ff4757', icon: XCircle },
      cancelled: { label: 'Cancelado', color: '#666', icon: AlertCircle },
      refunded: { label: 'Reembolsado', color: '#9b59b6', icon: AlertCircle }
    };

    return statusMap[status] || { label: status, color: '#666', icon: AlertCircle };
  };

  const getPaymentMethodLabel = (methodId: string) => {
    const methods: Record<string, string> = {
      credit_card: 'Cartão de Crédito',
      debit_card: 'Cartão de Débito',
      pix: 'PIX',
      boleto: 'Boleto',
      account_money: 'Saldo Mercado Pago'
    };

    return methods[methodId] || methodId;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const getAvailableYears = () => {
    const years = invoices.map(inv => new Date(inv.date_created).getFullYear());
    return Array.from(new Set(years)).sort((a, b) => b - a);
  };

  const calculateStats = () => {
    const total = filteredInvoices.reduce((sum, inv) => sum + inv.transaction_amount, 0);
    const approved = filteredInvoices.filter(inv => inv.status === 'approved').length;
    const pending = filteredInvoices.filter(inv => inv.status === 'pending').length;
    const rejected = filteredInvoices.filter(inv => inv.status === 'rejected').length;

    return { total, approved, pending, rejected };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <Container>
        <LoadingSpinner>
          <RefreshCw size={40} />
          <p>Carregando histórico de faturas...</p>
        </LoadingSpinner>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/billing/subscription')}>
          <ArrowLeft size={20} />
          Voltar para Assinatura
        </BackButton>
        <Title>Histórico de Faturas</Title>
        <Subtitle>Visualize todas as cobranças da sua assinatura</Subtitle>
      </Header>

      <StatsGrid>
        <StatCard>
          <StatIcon color="#00d4aa">
            <DollarSign size={24} />
          </StatIcon>
          <StatContent>
            <StatLabel>Total Pago</StatLabel>
            <StatValue>{formatCurrency(stats.total)}</StatValue>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon color="#00d4aa">
            <CheckCircle size={24} />
          </StatIcon>
          <StatContent>
            <StatLabel>Pagamentos Aprovados</StatLabel>
            <StatValue>{stats.approved}</StatValue>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon color="#ffa500">
            <Clock size={24} />
          </StatIcon>
          <StatContent>
            <StatLabel>Pendentes</StatLabel>
            <StatValue>{stats.pending}</StatValue>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon color="#ff4757">
            <XCircle size={24} />
          </StatIcon>
          <StatContent>
            <StatLabel>Rejeitados</StatLabel>
            <StatValue>{stats.rejected}</StatValue>
          </StatContent>
        </StatCard>
      </StatsGrid>

      <FiltersSection>
        <FilterGroup>
          <FilterIcon>
            <Filter size={18} />
          </FilterIcon>
          <FilterLabel>Filtros:</FilterLabel>

          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">Todos os Status</option>
            <option value="approved">Aprovados</option>
            <option value="pending">Pendentes</option>
            <option value="rejected">Rejeitados</option>
            <option value="cancelled">Cancelados</option>
          </Select>

          <Select value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
            <option value="all">Todos os Anos</option>
            {getAvailableYears().map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </Select>
        </FilterGroup>

        <ResultsCount>
          {filteredInvoices.length} {filteredInvoices.length === 1 ? 'fatura' : 'faturas'}
        </ResultsCount>
      </FiltersSection>

      {filteredInvoices.length === 0 ? (
        <EmptyState>
          <FileText size={60} />
          <h3>Nenhuma fatura encontrada</h3>
          <p>Não há faturas que correspondam aos filtros selecionados.</p>
        </EmptyState>
      ) : (
        <InvoicesTable>
          <TableHeader>
            <HeaderCell>Data</HeaderCell>
            <HeaderCell>Descrição</HeaderCell>
            <HeaderCell>Método</HeaderCell>
            <HeaderCell>Valor</HeaderCell>
            <HeaderCell>Status</HeaderCell>
            <HeaderCell>Ações</HeaderCell>
          </TableHeader>

          <TableBody>
            {filteredInvoices.map((invoice) => {
              const statusInfo = getStatusInfo(invoice.status);
              const StatusIcon = statusInfo.icon;

              return (
                <TableRow key={invoice.payment_id}>
                  <TableCell>
                    <CellWithIcon>
                      <Calendar size={16} color="#999" />
                      <div>
                        <div>{formatDate(invoice.date_created)}</div>
                        {invoice.date_approved && (
                          <SmallText>Aprovado: {formatDate(invoice.date_approved)}</SmallText>
                        )}
                      </div>
                    </CellWithIcon>
                  </TableCell>

                  <TableCell>
                    <div>{invoice.description || 'Pagamento de assinatura'}</div>
                    <SmallText>ID: {invoice.payment_id}</SmallText>
                  </TableCell>

                  <TableCell>
                    <CellWithIcon>
                      <CreditCard size={16} color="#999" />
                      <div>
                        <div>{getPaymentMethodLabel(invoice.payment_method_id)}</div>
                        {invoice.installments > 1 && (
                          <SmallText>{invoice.installments}x</SmallText>
                        )}
                      </div>
                    </CellWithIcon>
                  </TableCell>

                  <TableCell>
                    <Amount>{formatCurrency(invoice.transaction_amount)}</Amount>
                  </TableCell>

                  <TableCell>
                    <StatusBadge color={statusInfo.color}>
                      <StatusIcon size={14} />
                      {statusInfo.label}
                    </StatusBadge>
                  </TableCell>

                  <TableCell>
                    <ActionButton
                      onClick={() => window.open(`https://www.mercadopago.com.br/activities/receipt?payment_id=${invoice.payment_id}`, '_blank')}
                      title="Ver comprovante"
                    >
                      <Download size={16} />
                    </ActionButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </InvoicesTable>
      )}
    </Container>
  );
};

const Container = styled.div`
  padding: 2rem;
  width: 100%;
  max-width: none;
  margin: 0;
  min-height: 100vh;
  background: #f8f9fa;

  @media (max-width: 768px) {
    padding: 1.25rem 1rem;
  }
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #00d4aa;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  cursor: pointer;
  margin-bottom: 1rem;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #1f2937;
  margin: 0 0 0.5rem 0;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  color: #64748b;
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1.5rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
`;

const StatIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.color}20;
  border-radius: 8px;
  color: ${props => props.color};
  flex-shrink: 0;
`;

const StatContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const StatLabel = styled.span`
  font-size: 0.85rem;
  color: #64748b;
`;

const StatValue = styled.span`
  font-size: 1.5rem;
  color: #0f172a;
  font-weight: 600;
`;

const FiltersSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding: 1rem 1.5rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const FilterIcon = styled.div`
  color: #00d4aa;
`;

const FilterLabel = styled.span`
  color: #1f2937;
  font-weight: 600;
`;

const Select = styled.select`
  padding: 0.5rem 1rem;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #1f2937;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8fafc;
  }

  option {
    background: #ffffff;
    color: #1f2937;
  }
`;

const ResultsCount = styled.span`
  color: #64748b;
  font-size: 0.9rem;
`;

const InvoicesTable = styled.div`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;

  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 2fr 1.2fr 1fr 1fr 0.5fr;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;

  @media (max-width: 768px) {
    min-width: 760px;
    padding: 1rem;
  }
`;

const HeaderCell = styled.div`
  color: #64748b;
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
`;

const TableBody = styled.div``;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 2fr 1.2fr 1fr 1fr 0.5fr;
  gap: 1rem;
  padding: 1.5rem;
  border-bottom: 1px solid #e2e8f0;
  transition: background 0.2s;

  &:hover {
    background: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    min-width: 760px;
    padding: 1rem;
  }
`;

const TableCell = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: #1f2937;
  font-size: 0.9rem;
`;

const CellWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SmallText = styled.div`
  font-size: 0.75rem;
  color: #64748b;
  margin-top: 0.2rem;
`;

const Amount = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #00d4aa;
`;

const StatusBadge = styled.div<{ color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.8rem;
  background: ${props => props.color}20;
  color: ${props => props.color};
  border: 1px solid ${props => props.color};
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.8rem;
  width: fit-content;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #00d4aa;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #ccfbf1;
    transform: translateY(-2px);
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 400px;
  color: #00d4aa;

  svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  p {
    color: #64748b;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 300px;
  text-align: center;
  color: #64748b;
  padding: 3rem;

  svg {
    color: #666;
  }

  h3 {
    color: #0f172a;
    margin: 0;
  }

  p {
    margin: 0;
  }
`;
